// VAT reversals retain positive original source amounts and subtract the same
// source's tax bases on the reversal date. Rounding happens after aggregation.
// Norwegian SAF-T markers are journal information, not return lines (2022+).
// Source: Skatteetaten's VAT return information model; mirrored by
// functions/accounting/norwegianVatMapping.ts reportedInReturn.
const summarizeVatReturnCodes = (rows, country, year, rawAmounts = false) => {
  const excluded = country === 'NO' && Number(year) >= 2022 ? new Set(['0', '7', '20', '21', '22']) : new Set();
  const grouped = new Map();
  safeArray(rows).filter((row) => row && !excluded.has(String(row.code))).forEach((row) => {
    const code = String(row.code);
    const group = grouped.get(code) || { rows: [], rates: new Set(), kinds: new Set() };
    group.rows.push(row);
    group.rates.add(row.sats);
    if (row.kind) group.kinds.add(row.kind);
    grouped.set(code, group);
  });
  return [...grouped.entries()].map(([code, group]) => {
    const sum = (field) => { const total = group.rows.reduce((value, row) => value + num(row[field]), 0); return rawAmounts ? total : Math.round(total * 100) / 100; };
    return { ...group.rows[0], code, grunnlag: sum('grunnlag'), mva: sum('mva'),
      outgoing: group.rows.every((row) => row.outgoing !== undefined) ? sum('outgoing') : undefined,
      incoming: group.rows.every((row) => row.incoming !== undefined) ? sum('incoming') : undefined,
      sats: group.rates.size === 1 ? [...group.rates][0] : [...group.rates].join(' / '),
      kind: [...group.kinds].join(', '), reviewRequired: group.rows.some((row) => row.reviewRequired),
    };
  });
};
const deriveVat = (state, year, options = {}) => {
  const result = deriveVatDetails(state, year, options);
  return { ...result, codes: summarizeVatReturnCodes(result.codes, String(state.settings?.country || '').toUpperCase(), year, options.rawAmounts) };
};
// Ledger balances read model (ledgerBalances.js): the month documents carry the same VAT
// specification totals the journal scan sums, keyed code_rateHundredths_treatment.
const deriveVatFromLedgerBalances = (balances, year, options = {}) => {
  const roundVat = options.rawAmounts ? Number : Math.round;
  const rows = new Map();
  let outgoing = 0;
  let incoming = 0;
  for (const month of Object.keys(balances).sort()) {
    if (!reportDateInRange(`${month}-01`, year, options)) continue;
    for (const [key, totals] of Object.entries(balances[month].vat || {})) {
      const [code, rateHundredths, ...treatment] = key.split('_');
      if (!code) continue;
      const output = num(totals.output) / 100;
      const input = num(totals.deductibleInput) / 100;
      outgoing += output;
      incoming += input;
      const rowKey = `${code}:${num(rateHundredths) / 100}:${treatment.join('_') === 'NONE' ? '' : treatment.join('_')}`;
      const current = rows.get(rowKey) || { code, desc: `MVA-kode ${code}`, grunnlag: 0, sats: num(rateHundredths) / 100, mva: 0, outgoing: 0, incoming: 0, kind: treatment.join('_') === 'NONE' ? '' : treatment.join('_') };
      current.grunnlag += num(totals.base) / 100;
      current.outgoing += output;
      current.incoming += input;
      current.mva += output - input;
      rows.set(rowKey, current);
    }
  }
  const netVat = roundVat((outgoing - incoming) * 100) / 100;
  return {
    skyldig: Math.max(0, netVat), betalt: 0, inngaaende: roundVat(incoming * 100) / 100, utgaaende: roundVat(outgoing * 100) / 100, tilgode: Math.max(0, -netVat),
    codes: [...rows.values()].map((row) => ({ ...row, grunnlag: Math.round(row.grunnlag), mva: Math.round(row.mva), outgoing: Math.round(row.outgoing), incoming: Math.round(row.incoming) }))
      .filter((row) => row.grunnlag || row.mva || row.outgoing || row.incoming),
  };
};
const deriveVatDetails = (state, year, options = {}) => {
  const importedDecision = window.PlummeAccountingBasisCoverage.evaluate(state.accountingBasisCoverage, { allowImportedStatistics: true, period: { from: `${year}-01-01`, to: `${year}-12-31` } });
  if (importedDecision.readable && importedDecision.classification === 'INCOMPLETE') {
    const vat = importedVatSummary(state, year, options), netVat = vat.hasFilingEvidence ? vat.filingNetVat : vat.outgoing - vat.incoming;
    return { utgaaende: vat.outgoing, inngaaende: vat.incoming, netVat, skyldig: Math.max(0, netVat), tilgode: Math.max(0, -netVat), betalt: 0, codes: vat.codes, importedArchiveOnly: true };
  }
  const ledger = typeof ledgerBalancesFor === 'function' ? ledgerBalancesFor(state, year, options) : null;
  if (ledger) return deriveVatFromLedgerBalances(ledger, year, options);
  if (Array.isArray(state?.accountingEvents)) return deriveVatFromAccountingJournal(state, year, options);
  const fields = ['expenses', 'otherSales', 'customsDeclarations'];
  const originals = { ...state };
  const reversals = { settings: state.settings };
  let hasReversals = false;
  fields.forEach((field) => {
    originals[field] = safeArray(state[field]).map((entry) => entry.status === 'annulled' ? { ...entry, status: 'active' } : entry);
    reversals[field] = safeArray(state[field]).filter((entry) => entry.status === 'annulled').map((entry) => ({
      ...entry, status: 'active', date: globalThis.PlummeAccountingScope.accountingEventDate(state, entry.reversalDate || entry.annulledAt || entry.updatedAt || entry.date),
    }));
    hasReversals ||= reversals[field].length > 0;
  });
  if (!hasReversals) return deriveVatEventTotals(state, year, options);
  const original = deriveVatEventTotals(originals, year, { ...options, rawAmounts: true });
  const reversal = deriveVatEventTotals(reversals, year, { ...options, rawAmounts: true });
  const codes = new Map();
  [[original, 1], [reversal, -1]].forEach(([report, sign]) => report.codes.forEach((row) => {
    const key = `${row.code}:${row.sats}:${row.kind || ''}`;
    const total = codes.get(key) || { ...row, grunnlag: 0, mva: 0, outgoing: 0, incoming: 0 };
    total.grunnlag += sign * num(row.grunnlag);
    total.mva += sign * num(row.mva);
    total.outgoing += sign * num(row.outgoing, Math.max(0, num(row.mva)));
    total.incoming += sign * num(row.incoming, Math.max(0, -num(row.mva)));
    total.reviewRequired ||= row.reviewRequired;
    codes.set(key, total);
  }));
  const netVat = Math.round(original.netVat - reversal.netVat);
  return { ...original, netVat, skyldig: Math.max(0, netVat), tilgode: Math.max(0, -netVat),
    inngaaende: Math.round(original.inngaaende - reversal.inngaaende),
    utgaaende: Math.round(original.utgaaende - reversal.utgaaende),
    codes: [...codes.values()].map((row) => ({
      ...row,
      grunnlag: Math.round(row.grunnlag),
      mva: Math.round(row.mva),
      outgoing: Math.round(row.outgoing),
      incoming: Math.round(row.incoming),
    })).filter((row) => row.grunnlag || row.mva || row.outgoing || row.incoming),
  };
};

const deriveVatFromAccountingJournal = (state, year, options = {}) => {
  const roundVat = options.rawAmounts ? Number : Math.round;
  const rows = new Map();
  let outgoing = 0;
  let incoming = 0;
  safeArray(state.accountingEvents).forEach((event) => {
    if (!reportDateInRange(event.accounting_date, year, options)) return;
    safeArray(event.vat_specification).forEach((spec) => {
      const code = String(spec.standard_tax_code || '').trim();
      if (!code) return;
      const output = num(spec.output_vat_amount);
      const input = num(spec.deductible_input_vat_amount);
      outgoing += output;
      incoming += input;
      const key = `${code}:${num(spec.rate)}:${String(spec.treatment || '')}`;
      const current = rows.get(key) || {
        code,
        desc: `MVA-kode ${code}`,
        grunnlag: 0,
        sats: num(spec.rate),
        mva: 0,
        outgoing: 0,
        incoming: 0,
        kind: String(spec.treatment || ''),
      };
      current.grunnlag += ['14','15'].includes(code) && spec.treatment === 'vatOnly' ? 0 : num(spec.base_amount);
      current.outgoing += output;
      current.incoming += input;
      current.mva += output - input;
      rows.set(key, current);
    });
  });
  const netVat = roundVat((outgoing - incoming) * 100) / 100;
  return {
    skyldig: Math.max(0, netVat),
    betalt: 0,
    inngaaende: roundVat(incoming * 100) / 100,
    utgaaende: roundVat(outgoing * 100) / 100,
    tilgode: Math.max(0, -netVat),
    netVat,
    codes: [...rows.values()].map((row) => ({
      ...row,
      grunnlag: roundVat(row.grunnlag * 100) / 100,
      mva: roundVat(row.mva * 100) / 100,
      outgoing: roundVat(row.outgoing * 100) / 100,
      incoming: roundVat(row.incoming * 100) / 100,
    })).filter((row) => row.grunnlag || row.mva || row.outgoing || row.incoming),
  };
};

const deriveVatEventTotals = (state, year, options = {}) => {
  const roundVat = options.rawAmounts ? Number : Math.round;
  const country = String(state.settings?.country || 'NO').toUpperCase();
  const inRange = (entry) => reportDateInRange(entry.date, year, options);
  const expenseVatEvents = safeArray(state.expenses).flatMap((expense) => expense?.status === 'annulled'
    ? [{ expense: { ...expense, status: 'active' }, date: expense.date, factor: 1 }, { expense: { ...expense, status: 'active' }, date: globalThis.PlummeAccountingScope.accountingEventDate(state, expense.reversalDate || expense.annulledAt || expense.updatedAt || expense.date), factor: -1 }]
    : isActiveExpense(expense) ? [{ expense, date: expense.date, factor: 1 }] : []);
  const importedVat = importedVatSummary(state, year, options);
  const startupVat = startupVatSummary(state, year, options);
  const manualVat = safeArray(state.expenses)
    .filter((expense) =>
      inRange(expense) &&
      isActiveExpense(expense) &&
      expense.manualPosting &&
      safeArray(expense.manualPostingLines).length,
    )
    .reduce((sum, expense) => {
      const current = manualVatSummary(expense);
      sum.incoming += current.incoming;
      sum.outgoing += current.outgoing;
      sum.inputBase += current.inputBase;
      sum.outputBase += current.outputBase;
      current.rows.forEach((row) => {
        const key = `${row.code}:${row.rate}:${row.kind}`;
        const total = sum.rows.get(key) || { ...row, base: 0, incoming: 0, outgoing: 0 };
        total.base += row.base;
        total.incoming += row.incoming;
        total.outgoing += row.outgoing;
        sum.rows.set(key, total);
      });
      return sum;
  }, { incoming: 0, outgoing: 0, inputBase: 0, outputBase: 0, rows: new Map() });
  const saleVat = saleVatReportSummary(state, year, country, options);
  const reverseChargeRows = new Map();
  const reverseChargeVat = expenseVatEvents
    .filter((event) => inRange({ date: event.date }) && !event.expense.manualPosting)
    .reduce((sum, event) => {
      const expense = event.expense, factor = event.factor;
      const lines = safeArray(expense.purchaseLines).length ? safeArray(expense.purchaseLines) : [expense];
      lines.forEach((line) => {
        const code = String(line.reverseChargeCode || '');
        if (code !== '81' && code !== '86') return;
        const amountNet = Math.max(0, num(line.amountNet, expense.amountNet));
        const rate = num(line.vatRate, expense.vatRate);
        const breakdown = dashboardExpenseVatBreakdown({ ...expense, ...line, amountNet, vatRate: rate, vatTreatment: 'reverseCharge', reverseChargeCode: code, purchaseLines: [] });
        const current = reverseChargeRows.get(code) || {
          code,
          desc: code === '81' ? 'Varer kj\u00f8pt fra utlandet, snudd avgift' : 'Tjenester kj\u00f8pt fra utlandet, snudd avgift',
          grunnlag: 0,
          sats: rate,
          outgoing: 0,
          incoming: 0,
          kind: 'Omvendt avgiftsplikt',
        };
        current.grunnlag += amountNet * factor;
        current.outgoing += breakdown.reverseChargeOutputVat * factor;
        current.incoming += breakdown.deductibleVat * factor;
        reverseChargeRows.set(code, current);
        sum.outgoing += breakdown.reverseChargeOutputVat * factor;
        sum.incoming += breakdown.deductibleVat * factor;
      });
      return sum;
    }, { outgoing: 0, incoming: 0 });
  const outgoing = saleVat.outgoing + manualVat.outgoing + reverseChargeVat.outgoing;
  const incoming = expenseVatEvents
    .filter((event) => inRange({ date: event.date }) && !event.expense.manualPosting)
    .reduce((sum, event) => sum + dashboardExpenseVatBreakdown(event.expense).deductibleVat * event.factor, 0) +
    manualVat.incoming;
  const purchaseVatByRate = new Map();
  expenseVatEvents
    .filter((event) => inRange({ date: event.date }) && !event.expense.manualPosting)
    .forEach((event) => {
      const expense = event.expense, factor = event.factor;
      const lines = safeArray(expense.purchaseLines).length ? safeArray(expense.purchaseLines) : [expense];
      lines.forEach((line) => {
        const rate = num(line.vatRate, expense.vatRate);
        const treatment = line.vatTreatment || expense.vatTreatment || (rate === 0 ? 'zeroRated' : 'standard');
        if (!['standard', 'vatOnly'].includes(treatment) || rate <= 0) return;
        const amountNet = Math.max(0, num(line.amountNet, expense.amountNet));
        const breakdown = dashboardExpenseVatBreakdown({ ...expense, ...line, amountNet, vatRate: rate, vatTreatment: treatment, purchaseLines: [] });
        if (breakdown.deductibleVat <= 0) return;
        const purchaseCode = treatment === 'vatOnly' && String(line.purchaseVatCode || expense.purchaseVatCode || '') === '14'
          ? '14'
          : rate === 25 ? '1' : rate === 15 ? '11' : rate === 12 ? '13' : 'review';
        const key = `${purchaseCode}:${rate}`;
        const current = purchaseVatByRate.get(key) || { code: purchaseCode, rate, base: 0, vat: 0 };
        current.base += treatment === 'vatOnly' ? 0 : amountNet * factor;
        current.vat += breakdown.deductibleVat * factor;
        purchaseVatByRate.set(key, current);
      });
    });
  const customsRows = new Map();
  const customsVat = safeArray(state.customsDeclarations)
    .filter((entry) => inRange(entry) && entry.status !== 'annulled')
    .reduce((sum, entry) => {
      const code = String(entry.customsVatCode || '81');
      const rate = code === '83' || code === '84' ? 15 : code === '85' ? 0 : 25;
      const outgoingVat = code === '85' ? 0 : num(entry.outgoingVat ?? entry.outputVat ?? entry.vatAmount);
      const deductibleVat = code === '81' || code === '83' ? num(entry.deductibleVat ?? entry.inputVat ?? entry.vatAmount) : 0;
      const current = customsRows.get(code) || {
        code,
        desc: code === '85' ? 'Innførsel av varer uten beregnet MVA' : `Innførsel av varer, kode ${code}`,
        grunnlag: 0,
        sats: rate,
        mva: 0,
        hint: 'Registrert tolldeklarasjon fra app-state.',
        kind: 'Innførsel',
        outgoing: 0,
        incoming: 0,
      };
      current.grunnlag += num(entry.customsBasis);
      current.mva += outgoingVat - deductibleVat;
      current.outgoing += outgoingVat;
      current.incoming += deductibleVat;
      customsRows.set(code, current);
      sum.outgoing += outgoingVat;
      sum.incoming += deductibleVat;
      return sum;
    }, { outgoing: 0, incoming: 0 });
  const totalOutgoing = outgoing + customsVat.outgoing + importedVat.outgoing + startupVat.outgoing;
  const totalIncoming = incoming + customsVat.incoming + importedVat.incoming + startupVat.incoming;
  const importedNetVat = importedVat.hasFilingEvidence
    ? importedVat.filingNetVat
    : importedVat.outgoing - importedVat.incoming;
  const totalNetVat = roundVat((outgoing + customsVat.outgoing + startupVat.outgoing - incoming - customsVat.incoming - startupVat.incoming + importedNetVat) * 100) / 100;
  const codes = country === 'NO'
    ? [
        ...Object.entries(saleVat.taxableByRate)
          .filter(([rate, base]) => num(rate) > 0 && Math.abs(num(base)) > 0.005)
          .map(([rate, base]) => {
            const numericRate = num(rate);
            const code = numericRate === 25 ? '3' : numericRate === 15 ? '31' : numericRate === 12 ? '33' : 'review';
            return {
              code,
              desc: code === '3' ? 'Utgående MVA, høy sats' : code === '31' ? 'Utgående MVA, middels sats' : code === '33' ? 'Utgående MVA, lav sats' : `Utgående MVA, ukjent sats ${numericRate} %`,
              grunnlag: roundVat(num(base)),
              sats: numericRate,
              mva: roundVat(num(base) * numericRate / 100),
              reviewRequired: code === 'review',
            };
          }),
        ...[...manualVat.rows.values()].map((row) => ({
          code: row.code || 'review',
          desc: row.kind === 'input' ? 'Manuell inngående MVA' : row.kind === 'output' ? 'Manuell utgående MVA' : row.kind === 'basis' ? 'Manuelt MVA-grunnlag' : 'Manuell omvendt avgiftsplikt',
          grunnlag: roundVat(row.base),
          sats: row.rate,
          mva: roundVat(row.outgoing - row.incoming),
          outgoing: roundVat(row.outgoing),
          incoming: roundVat(row.incoming),
          kind: 'Manuell postering',
          reviewRequired: !row.code,
        })),
        saleVat.zeroRatedBase ? { code: '5', desc: 'Salg med nullsats', grunnlag: roundVat(saleVat.zeroRatedBase), sats: 0, mva: 0 } : null,
        saleVat.reverseChargeSalesBase ? { code: '51', desc: 'Innenlandsk omsetning med omvendt avgiftsplikt', grunnlag: roundVat(saleVat.reverseChargeSalesBase), sats: 0, mva: 0 } : null,
        saleVat.exportBase ? { code: '52', desc: 'Eksport og utførsel', grunnlag: roundVat(saleVat.exportBase), sats: 0, mva: 0 } : null,
        saleVat.exemptBase ? { code: '6', desc: 'Omsetning utenfor MVA-loven', grunnlag: roundVat(saleVat.exemptBase), sats: 0, mva: 0 } : null,
        ...[...purchaseVatByRate.values()].map((value) => {
          const { code, rate } = value;
          return {
            code,
            desc: code === '14' ? 'Fradragsberettiget innførsels-MVA, høy sats' : code === '1' ? 'Fradragsberettiget inngående MVA, høy sats' : code === '11' ? 'Fradragsberettiget inngående MVA, middels sats' : code === '13' ? 'Fradragsberettiget inngående MVA, lav sats' : `Inngående MVA, ukjent sats ${rate} %`,
            grunnlag: roundVat(value.base),
            sats: rate,
            mva: -roundVat(value.vat),
            reviewRequired: code === 'review',
          };
        }),
        ...[...reverseChargeRows.values()].map((row) => ({
          code: row.code,
          desc: row.desc,
          grunnlag: roundVat(row.grunnlag),
          sats: row.sats,
          mva: roundVat(row.outgoing - row.incoming),
          outgoing: roundVat(row.outgoing),
          incoming: roundVat(row.incoming),
          kind: row.kind,
        })),
        ...[...customsRows.values()].map((row) => ({
          ...row,
          grunnlag: roundVat(row.grunnlag),
          mva: roundVat(row.mva),
          outgoing: roundVat(row.outgoing),
          incoming: roundVat(row.incoming),
        })),
        ...importedVat.codes,
        ...startupVat.codes,
      ].filter(Boolean)
    : [
        { code: 'sales', desc: `${countryVatLabel(country)}pliktig salg`, grunnlag: roundVat(saleVat.standardBase), sats: standardVatForCountry(country), mva: roundVat(saleVat.outgoing) },
        saleVat.zeroRatedBase ? { code: 'zero', desc: `0 % / ${countryVatLabel(country)}fritt salg`, grunnlag: roundVat(saleVat.zeroRatedBase), sats: 0, mva: 0 } : null,
        saleVat.exemptBase ? { code: 'outside', desc: `Salg utenfor ${countryVatLabel(country)}`, grunnlag: roundVat(saleVat.exemptBase), sats: 0, mva: 0 } : null,
        { code: 'purchases', desc: `Fradragsført inngående ${countryVatLabel(country)}`, grunnlag: roundVat([...purchaseVatByRate.values()].reduce((sum, value) => sum + value.base, 0)), sats: standardVatForCountry(country), mva: -roundVat(incoming) },
        ...startupVat.codes,
      ].filter(Boolean);
  return {
    skyldig: Math.max(0, roundVat(totalNetVat)),
    betalt: 0,
    inngaaende: roundVat(totalIncoming),
    utgaaende: roundVat(totalOutgoing),
    tilgode: Math.max(0, -roundVat(totalNetVat)),
    netVat: roundVat(totalNetVat),
    codes,
  };
};
